import { FC, memo, useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Button, CircularProgress, Modal } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import clsx from 'clsx';
import {
  SuggestedTaskItem,
  TaskItem,
  TaskItemDuration,
  TaskLabel,
  TeamMember,
  TeamTaskMemberSummary,
  TodoTasksBoard,
} from '@demind-inc/core';
import dayjs from 'dayjs';
import { useLocalStorage } from '@uidotdev/usehooks';
import { v4 as uuidv4 } from 'uuid';
import './TaskDetailModal.scss';
import {
  circadianPhaseInfo,
  DEFAULT_TASK_DURATION_MIN,
  AddTodoTaskItemProps,
  useAuthContext,
  useCircadianContext,
  useTaskActions,
  useTodoTasksContext,
  TodoTaskUpdate,
} from '../../data-access';
import { DateTimeSetOnlyTime } from './TaskDueDatePicker';
import {
  formatTaskTimeRangeFromDuration,
  getDefaultBoardId,
  getDueDateYYYYMMDD,
  getIsOverdue,
  getStartDateTimeFromDuration,
} from '../../helpers';
import { isEmpty, omit, isEqual } from 'lodash';
import { TaskProject } from './TaskProject';
import {
  useAddProject,
  useGeneralSettings,
  useMoveTaskBetweenProjects,
  useProjectFilter,
  useTaskSettings,
} from '../../hooks';
import { AddProjectDialog } from '../Project';
import { TaskLabelsSelect } from './TaskLabelsSelect';
import BasicDialog from '../common/BasicDialog/BasicDialog';
import { PriorityFlag, PrioritySelect } from '../Priority';
import { getCssVariable } from '../../utils';
import {
  TaskTitleDescriptionSection,
  TaskDescriptionField,
} from './TaskTitleDescriptionSection';
import { TaskScheduleSection, RepeatSelect } from './TaskScheduleSection';
import { TaskPropertyRow } from './TaskPropertyRow';
import { TaskSubtasksSection } from './TaskSubtasksSection';
import { TaskAiNoteSection } from './TaskAiNoteSection';
import {
  normalizeRecurringString,
  isCustomRecurrence,
} from './TaskScheduleSection/recurrenceHelpers';
import { mdiInboxOutline, mdiTagOutline } from '@mdi/js';
import Icon from '@mdi/react';
import {
  DeleteOutlined,
  PersonOutlineRounded,
  RepeatOutlined as RepeatIcon,
} from '@mui/icons-material';
import { isAppleRemindersDesktopSupported } from '../../helpers/appleRemindersDesktop.helpers';
import TeamTaskAssigneeSelect from '../Team/TeamTaskAssigneeSelect';
import { useTranslation } from 'react-i18next';
import {
  buildCreateTaskPayload,
  buildMovedTaskParam,
  buildUpdateTaskPayload,
} from './taskDetailModalSave.helpers';

export interface TeamTaskModalSaveParams {
  taskId?: string;
  boardId: string;
  taskInfo: TaskDetail;
  assigneeUserId: string | null;
  selectedProject: TodoTasksBoard | null;
}

export interface TeamTaskModalCompleteParams {
  taskId: string;
  boardId: string;
  completed: boolean;
}

export interface TeamTaskModalDeleteParams {
  taskId: string;
  boardId: string;
}

export interface TeamTaskModalAssignParams {
  taskId: string;
  boardId: string;
  assigneeUserId: string | null;
}

interface TeamTaskModalOptions {
  boards: TodoTasksBoard[];
  members: Array<TeamTaskMemberSummary & Pick<Partial<TeamMember>, 'status'>>;
  currentUserId: string;
  assigneeUserId?: string;
  isSaving?: boolean;
  isDeleting?: boolean;
  onSaveTask: (params: TeamTaskModalSaveParams) => Promise<void>;
  onCompleteTask?: (params: TeamTaskModalCompleteParams) => Promise<void>;
  onDeleteTask?: (params: TeamTaskModalDeleteParams) => Promise<void>;
  onAssignTask?: (params: TeamTaskModalAssignParams) => Promise<void>;
  onOpenCreateBoard?: () => void;
}

interface TaskDetailModalProps {
  visible: boolean;
  task?: TaskItem;
  onClose: () => void;
  isReadOnly?: boolean;
  readOnlyBadgeText?: string;
  boardNameOverride?: string;
  isCreateMode?: boolean;
  teamTaskOptions?: TeamTaskModalOptions;
}

export type TaskDetail = Pick<
  TaskItem,
  | 'name'
  | 'desc'
  | 'aiNote'
  | 'dueDateTime'
  | 'duration'
  | 'boardId'
  | 'labels'
  | 'sortIndex'
  | 'addedToCalendar'
  | 'subTasks'
  | 'priority'
  | 'recurring'
>;

type PersistedTaskSnapshot = Pick<TaskItem, 'name' | 'dueDateTime' | 'duration' | 'labels' | 'priority'>;

type TaskFormSnapshot = {
  name: string;
  desc: string;
  aiNote: string;
  dueDate: string;
  dueDatetime: string;
  duration: TaskItemDuration;
  labels: TaskLabel[];
  priority: TaskItem['priority'];
  recurring: string | null;
  assigneeUserId: string;
};

const toPersistedTaskSnapshot = (task: {
  name?: string;
  dueDateTime?: TaskItem['dueDateTime'];
  duration?: TaskItem['duration'];
  labels?: TaskLabel[];
  priority?: TaskItem['priority'];
}): PersistedTaskSnapshot => ({
  name: task.name,
  dueDateTime: task.dueDateTime,
  duration: task.duration,
  labels: task.labels,
  priority: task.priority,
});

const toTaskFormSnapshot = (params: {
  name: string;
  desc: string;
  aiNote?: string;
  dueDateTime?: TaskItem['dueDateTime'];
  duration: TaskItemDuration;
  labels?: TaskLabel[];
  priority?: TaskItem['priority'];
  recurring?: string | null;
  assigneeUserId?: string;
}): TaskFormSnapshot => ({
  name: params.name.trim(),
  desc: params.desc.trim(),
  aiNote: params.aiNote || '',
  dueDate: params.dueDateTime?.date || '',
  dueDatetime: params.dueDateTime?.datetime || '',
  duration: params.duration,
  labels: params.labels ?? [],
  priority: params.priority,
  recurring: params.recurring ?? null,
  assigneeUserId: params.assigneeUserId || '',
});

const TaskDetailModal: FC<TaskDetailModalProps> = memo(
  ({
    visible,
    task: defaultTask,
    onClose,
    isReadOnly: isReadOnlyProp = false,
    readOnlyBadgeText,
    boardNameOverride,
    isCreateMode = false,
    teamTaskOptions,
  }) => {
    const { t } = useTranslation();
    const isTeamTaskMode = !!teamTaskOptions;
    const isEditMode = !isCreateMode && !isEmpty(omit(defaultTask, ['dueDateTime', 'sortIndex']));
    const showDiscardButton =
      isEditMode &&
      !isReadOnlyProp &&
      !isCreateMode &&
      (!isTeamTaskMode || !!teamTaskOptions?.onDeleteTask) &&
      !(defaultTask?.appFrom === 'apple-reminders' && !isAppleRemindersDesktopSupported()) &&
      !defaultTask?.completed;
    const { user } = useAuthContext();
    const { findProject, visibleProjectIds, todoProjects, taskLabels } = useTodoTasksContext();
    const { findPhaseForTaskTime } = useCircadianContext();
    const [lastUsedBoardId, setLastUsedBoardId] = useLocalStorage(
      'lifestack_last_used_board_id',
      ''
    );
    const { selectedFilters, filterProjectOptions } = useProjectFilter();

    const {
      isUpdatingTask: isUpdating,
      deleteTask,
      isDeletingTask: isDeleting,
      addTodoTask,
      updateTodoTask,
      isAddingTask,
      isTaskStatusUpdating,
      updateTodoTaskStatus,
    } = useTaskActions();
    const { moveTaskBetweenProjects, isMovingTaskBetweenProjects } = useMoveTaskBetweenProjects();

    const { projectDialogVisible, isAddingProject, handleAddProject, handleProjectDialogVisible } =
      useAddProject();

    const resolvedTodoProjects = teamTaskOptions?.boards ?? todoProjects;
    const defaultBoardId = isTeamTaskMode
      ? defaultTask?.boardId || resolvedTodoProjects[0]?.todoTaskId || ''
      : getDefaultBoardId({
          defaultTask,
          selectedFilters,
          lastUsedBoardId,
          filterProjectOptions,
        });

    const initialRecurringText = useMemo(
      () => (defaultTask ? normalizeRecurringString(defaultTask.recurring) : null),
      [defaultTask?.recurring]
    );
    const [taskInfo, setTaskInfo] = useState<TaskDetail>({
      name: defaultTask?.name || '',
      desc: defaultTask?.desc || '',
      aiNote: defaultTask?.aiNote || '',
      boardId: defaultBoardId,
      dueDateTime: defaultTask?.dueDateTime || { timezone: dayjs.tz.guess() },
      duration: defaultTask?.duration || { amount: DEFAULT_TASK_DURATION_MIN, unit: 'minute' },
      labels: defaultTask?.labels ?? [],
      sortIndex: defaultTask?.sortIndex,
      subTasks: defaultTask?.subTasks ?? [],
      priority: defaultTask?.priority,
      recurring: initialRecurringText
        ? {
            isRecurring: true,
            string: initialRecurringText,
            lang: defaultTask?.recurring?.lang || 'en',
            timezone: defaultTask?.recurring?.timezone || dayjs.tz.guess(),
          }
        : undefined,
    });
    const [teamTaskAssigneeUserId, setTeamTaskAssigneeUserId] = useState(
      teamTaskOptions?.assigneeUserId || ''
    );
    const {
      generalSettings: { timeFormat },
    } = useGeneralSettings();
    const {
      taskSettings: { durationMin },
    } = useTaskSettings();
    const [aiSuggestedTask, setAiSuggestedTask] = useState<SuggestedTaskItem | null>(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [draftName, setDraftName] = useState(defaultTask?.name || '');
    const [draftDesc, setDraftDesc] = useState(defaultTask?.desc || '');
    const [initialFormSnapshot, setInitialFormSnapshot] = useState<TaskFormSnapshot | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const lastPersistedTaskRef = useRef<PersistedTaskSnapshot | null>(null);
    const isSubmittingRef = useRef(false);
    const selectedProject = useMemo(() => {
      const matchedProject = resolvedTodoProjects?.find(
        (project) => project.todoTaskId === taskInfo?.boardId
      );
      if (matchedProject) {
        return matchedProject;
      }

      if (taskInfo?.boardId && boardNameOverride && defaultTask?.appFrom) {
        return {
          todoTaskId: taskInfo.boardId,
          originalBoardId: taskInfo.boardId,
          name: boardNameOverride,
          appFrom: defaultTask.appFrom,
          createdAt: '',
        } as TodoTasksBoard;
      }

      return null;
    }, [boardNameOverride, defaultTask?.appFrom, resolvedTodoProjects, taskInfo?.boardId]);
    const currentTaskAppFrom = defaultTask?.appFrom || selectedProject?.appFrom;
    const isClickUpTask = currentTaskAppFrom === 'clickup';

    useEffect(() => {
      if (defaultTask) {
        const recurringText = normalizeRecurringString(defaultTask.recurring);
        const timezone = defaultTask.dueDateTime?.timezone || dayjs.tz.guess();
        const nextDuration = defaultTask.duration || {
          amount: durationMin,
          unit: 'minute' as const,
        };
        const nextDueDateTime = defaultTask.dueDateTime || { timezone };
        const nextName = defaultTask.name || '';
        const nextDesc = defaultTask.desc || '';
        const nextAiNote = defaultTask.aiNote || '';
        const nextLabels = defaultTask.labels ?? [];
        const nextAssigneeUserId = teamTaskOptions?.assigneeUserId || '';

        setTaskInfo({
          name: nextName,
          desc: nextDesc,
          aiNote: nextAiNote,
          boardId: defaultBoardId,
          dueDateTime: nextDueDateTime,
          duration: nextDuration,
          labels: nextLabels,
          sortIndex: defaultTask.sortIndex,
          addedToCalendar: defaultTask.addedToCalendar,
          subTasks: defaultTask.subTasks ?? [],
          priority: defaultTask.priority,
          recurring: recurringText
            ? {
                isRecurring: true,
                string: recurringText,
                lang: defaultTask.recurring?.lang || 'en',
                timezone: defaultTask.recurring?.timezone || timezone,
              }
            : undefined,
        });
        lastPersistedTaskRef.current = toPersistedTaskSnapshot({
          name: nextName,
          dueDateTime: nextDueDateTime,
          duration: nextDuration,
          labels: nextLabels,
          priority: defaultTask.priority,
        });
        setDraftName(nextName);
        setDraftDesc(nextDesc);
        setInitialFormSnapshot(
          toTaskFormSnapshot({
            name: nextName,
            desc: nextDesc,
            aiNote: nextAiNote,
            dueDateTime: nextDueDateTime,
            duration: nextDuration,
            labels: nextLabels,
            priority: defaultTask.priority,
            recurring: recurringText,
            assigneeUserId: nextAssigneeUserId,
          })
        );
      } else if (isCreateMode) {
        setDraftName('');
        setDraftDesc('');
        setInitialFormSnapshot(null);
      }
      setAiSuggestedTask(null);
    }, [
      JSON.stringify(defaultTask),
      defaultBoardId,
      durationMin,
      isCreateMode,
      teamTaskOptions?.assigneeUserId,
    ]);

    useEffect(() => {
      setTeamTaskAssigneeUserId(teamTaskOptions?.assigneeUserId || '');
    }, [teamTaskOptions?.assigneeUserId, defaultTask?.taskId, visible]);

    const isAppleRemindersTask = useMemo(
      () => defaultTask?.appFrom === 'apple-reminders',
      [defaultTask]
    );

    const isAppleRemindersReadOnly = isAppleRemindersTask && !isAppleRemindersDesktopSupported();

    const isRecurringSupportApp =
      ['todoist', 'ticktick', 'lifestack'].includes(currentTaskAppFrom || '') ||
      (isAppleRemindersTask && isAppleRemindersDesktopSupported());
    const canEditRepeat = !defaultTask?.completed && isRecurringSupportApp;
    const shouldShowRepeatSelector =
      !!(taskInfo.dueDateTime?.date || taskInfo.dueDateTime?.datetime) && isRecurringSupportApp;

    const isCompletedRecurringTask = useMemo(
      () =>
        defaultTask?.completed &&
        (defaultTask?.recurring?.isRecurring || !!defaultTask?.recurring?.string),
      [defaultTask]
    );
    const isReadOnly = isAppleRemindersReadOnly || isCompletedRecurringTask || isReadOnlyProp;
    const isSavingTask = teamTaskOptions?.isSaving ?? isUpdating;
    const isDeletingVisibleTask = teamTaskOptions?.isDeleting ?? isDeleting;
    const resolvedReadOnlyBadgeText = isCompletedRecurringTask
      ? t('task.modal.completedOccurrenceViewOnly')
      : isReadOnlyProp
        ? readOnlyBadgeText || t('task.modal.viewOnly')
        : null;

    const hasCustomRecurrence = useMemo(
      () => isCustomRecurrence(taskInfo.recurring?.string ?? null),
      [taskInfo.recurring?.string]
    );
    const hasRecurringRule = useMemo(
      () => !!(taskInfo.recurring?.isRecurring || taskInfo.recurring?.string),
      [taskInfo.recurring]
    );

    const buildRecurringPayload = (recurrenceText: string | null) => {
      if (!recurrenceText) {
        return { isRecurring: false, string: '', lang: 'en', timezone: dayjs.tz.guess() };
      }
      return {
        isRecurring: true,
        string: recurrenceText,
        lang: taskInfo.recurring?.lang || defaultTask?.recurring?.lang || 'en',
        timezone:
          taskInfo.recurring?.timezone ||
          taskInfo.dueDateTime?.timezone ||
          defaultTask?.recurring?.timezone ||
          dayjs.tz.guess(),
      };
    };

    const handleProjectSelect = useCallback(
      async (boardId: string) => {
        if (isReadOnly) {
          return;
        }

        if (isTeamTaskMode) {
          if (!isEditMode) {
            setTaskInfo((prev) => ({ ...prev, boardId }));
          }
          return;
        }

        const targetProject = resolvedTodoProjects?.find(
          (project) => project.todoTaskId === boardId
        );

        if (
          hasRecurringRule &&
          targetProject?.appFrom === 'clickup' &&
          currentTaskAppFrom !== 'clickup'
        ) {
          return;
        }

        const currentBoardId = isEditMode ? defaultTask?.boardId : taskInfo.boardId;

        if (!boardId || !currentBoardId || boardId === currentBoardId) {
          if (!isEditMode) {
            setTaskInfo((prev) => ({ ...prev, boardId }));
          }
          return;
        }

        if (isEditMode && defaultTask?.taskId) {
          const mergedTask: TaskItem = {
            ...defaultTask,
            ...taskInfo,
            boardId: defaultTask.boardId,
          };

          const success = await moveTaskBetweenProjects({
            task: mergedTask,
            targetBoardId: boardId,
          });

          if (success) {
            onClose();
          }
          return;
        }

        setTaskInfo((prev) => ({ ...prev, boardId }));
      },
      [
        isReadOnly,
        isTeamTaskMode,
        hasRecurringRule,
        currentTaskAppFrom,
        resolvedTodoProjects,
        isEditMode,
        defaultTask,
        taskInfo,
        moveTaskBetweenProjects,
        onClose,
      ]
    );

    const handleTask = async (name: string, desc: string) => {
      if (!user?.userId || isSubmittingRef.current) {
        return;
      }

      isSubmittingRef.current = true;
      setIsSubmitting(true);

      const aiNote = taskInfo.aiNote || '';
      const recurringFallback = taskInfo.recurring || buildRecurringPayload(null);

      const abortSubmit = () => {
        isSubmittingRef.current = false;
        setIsSubmitting(false);
      };

      try {
        if (isTeamTaskMode && teamTaskOptions) {
          const nextTaskInfo = {
            ...taskInfo,
            name: name || taskInfo.name,
            desc: desc || taskInfo.desc,
            aiNote,
          };

          if (!nextTaskInfo.name.trim() || !nextTaskInfo.boardId) {
            abortSubmit();
            return;
          }

          setTaskInfo(nextTaskInfo);
          handleClose();

          try {
            await teamTaskOptions.onSaveTask({
              taskId: isEditMode ? defaultTask?.taskId : undefined,
              boardId: nextTaskInfo.boardId,
              taskInfo: nextTaskInfo,
              assigneeUserId: teamTaskAssigneeUserId || null,
              selectedProject,
            });
          } catch (error) {
            console.error('Failed to save team task:', error);
          }
          return;
        }

        setLastUsedBoardId(taskInfo.boardId);

        if (!isEditMode) {
          const { payload, shouldReflectToEvent } = buildCreateTaskPayload({
            name,
            desc,
            aiNote,
            taskInfo,
            appFrom: selectedProject?.appFrom || 'lifestack',
            isClickUpTask,
            recurringFallback,
          });
          const taskId = uuidv4().replace(/-/g, '');

          setTaskInfo((prev) => ({
            ...prev,
            name: name || prev.name,
            desc: desc || prev.desc,
            aiNote,
          }));
          handleClose();

          try {
            await addTodoTask({
              boardId: taskInfo.boardId,
              newTaskInfo: payload,
              taskId,
              shouldReflectToEvent,
            });
          } catch (error) {
            console.error('Failed to create task:', error);
          }
          return;
        }

        if (!defaultTask?.taskId) {
          abortSubmit();
          return;
        }

        const persistedPrevTaskInfo =
          lastPersistedTaskRef.current ??
          toPersistedTaskSnapshot({
            name: defaultTask.name,
            dueDateTime: defaultTask.dueDateTime,
            duration: defaultTask.duration,
          });
        const prevDate = getDueDateYYYYMMDD(persistedPrevTaskInfo.dueDateTime);
        const newDate = getDueDateYYYYMMDD(taskInfo.dueDateTime);
        const shouldAddToCalendar =
          !!taskInfo.dueDateTime?.datetime && !!taskInfo.duration?.amount;
        const hasRecurringChanged =
          !isClickUpTask && (taskInfo.recurring?.string || null) !== initialRecurringText;
        const movedTaskParam = buildMovedTaskParam(prevDate, newDate);
        const { newTaskInfo } = buildUpdateTaskPayload({
          name,
          desc,
          aiNote,
          taskInfo,
          defaultTask,
          isClickUpTask,
          recurringFallback,
          shouldAddToCalendar,
          findPhaseForTaskTime,
        });
        const nextPersistedSnapshot = toPersistedTaskSnapshot({
          ...taskInfo,
          name: name || taskInfo.name,
        });

        setTaskInfo((prev) => ({
          ...prev,
          name: name || prev.name,
          desc: desc || prev.desc,
          aiNote,
        }));
        handleClose();

        try {
          await updateTodoTask({
            boardId: taskInfo.boardId,
            taskId: defaultTask.taskId,
            newTaskInfo,
            prevTaskInfo: persistedPrevTaskInfo,
            recurrenceChanged: hasRecurringChanged,
            ...(movedTaskParam ? { movedTaskParam } : {}),
            shouldReflectToEvent: true,
          });
          lastPersistedTaskRef.current = nextPersistedSnapshot;
        } catch (error) {
          console.error('Failed to update task:', error);
        }
      } finally {
        isSubmittingRef.current = false;
        setIsSubmitting(false);
      }
    };

    const handleUpdateTaskStatus = async (
      taskId: string,
      completed: boolean,
      parentOriginalTaskId?: string
    ) => {
      try {
        if (isTeamTaskMode && teamTaskOptions?.onCompleteTask) {
          await teamTaskOptions.onCompleteTask({
            taskId,
            boardId: taskInfo.boardId,
            completed,
          });

          if (taskId === defaultTask?.taskId) {
            onClose();
          }
          return;
        }

        const dueDate = getDueDateYYYYMMDD(taskInfo.dueDateTime);
        updateTodoTaskStatus({
          taskId,
          completed,
          boardId: taskInfo.boardId,
          originalTaskId: defaultTask?.originalTaskId,
          mutateParams: {
            date: dueDate,
            parentOriginalTaskId,
          },
        });

        // Only close modal if updating the main task, not subtasks
        if (taskId === defaultTask?.taskId) {
          onClose();
        } else {
          // update subtask status
          setTaskInfo((prev) => ({
            ...prev,
            subTasks: prev.subTasks.map((subtask) =>
              subtask.taskId === taskId ? { ...subtask, completed } : subtask
            ),
          }));
        }
      } catch (error) {
        console.error('Failed to update task status:', error);
      }
    };

    const taskTime = isEditMode
      ? formatTaskTimeRangeFromDuration({
          duration: taskInfo.duration,
          dueDateTime: taskInfo.dueDateTime,
          timeFormat,
        })
      : '';

    const phase = useMemo(() => {
      if (!taskInfo.dueDateTime?.datetime || !taskInfo.duration.amount) return null;

      const startDateTime = getStartDateTimeFromDuration(
        taskInfo.duration,
        taskInfo.dueDateTime
      ).startDateTime;

      const targetPhase = findPhaseForTaskTime(
        startDateTime.datetime,
        taskInfo.dueDateTime.datetime
      );
      return targetPhase ? circadianPhaseInfo[targetPhase] : null;
    }, [taskInfo, findPhaseForTaskTime]);

    const shouldHideSmartScheduleButton = useMemo(() => {
      const dueDate = getDueDateYYYYMMDD(taskInfo.dueDateTime);
      if (!dueDate) return true;
      return getIsOverdue(dueDate);
    }, [taskInfo.dueDateTime]);

    const handleDeleteTask = async (taskId: string, parentOriginalTaskId?: string) => {
      try {
        if (isTeamTaskMode && teamTaskOptions?.onDeleteTask) {
          await teamTaskOptions.onDeleteTask({
            taskId,
            boardId: taskInfo.boardId,
          });

          if (taskId === defaultTask?.taskId) {
            onClose();
          }
          return;
        }

        deleteTask({
          taskId,
          boardId: taskInfo.boardId,
          originalTaskId: defaultTask?.originalTaskId,
          mutateParam: {
            date: getDueDateYYYYMMDD(taskInfo.dueDateTime),
            isOverdue: getIsOverdue(taskInfo.dueDateTime.datetime || taskInfo.dueDateTime.date),
            parentOriginalTaskId,
          },
          shouldReflectToEvent: true,
          dueDateTime: taskInfo.dueDateTime,
        });

        // Only close modal if updating the main task, not subtasks
        if (taskId === defaultTask?.taskId) {
          onClose();
        } else {
          // delete subtask
          setTaskInfo((prev) => ({
            ...prev,
            subTasks: prev.subTasks.filter((subtask) => subtask.taskId !== taskId),
          }));
        }
      } catch (error) {
        console.error('Failed to delete task:', error);
      }
    };

    const handleClose = () => {
      if (isMovingTaskBetweenProjects) {
        return;
      }

      onClose();
      setTaskInfo({
        name: '',
        desc: '',
        aiNote: '',
        boardId: '',
        dueDateTime: { timezone: dayjs.tz.guess() },
        duration: { amount: 0, unit: 'minute' },
        subTasks: [],
        recurring: undefined,
      });
      setDraftName('');
      setDraftDesc('');
      setAiSuggestedTask(null);
    };

    const handleCancel = () => {
      setDraftName(taskInfo.name || defaultTask?.name || '');
      setDraftDesc(taskInfo.desc || defaultTask?.desc || '');
      handleClose();
    };

    const isCreateSaving = isTeamTaskMode && isCreateMode ? isSavingTask : isAddingTask;
    const hasUnsavedChanges = useMemo(() => {
      if (!isEditMode || !initialFormSnapshot) {
        return true;
      }

      const current = toTaskFormSnapshot({
        name: draftName,
        desc: draftDesc,
        aiNote: taskInfo.aiNote,
        dueDateTime: taskInfo.dueDateTime,
        duration: taskInfo.duration,
        labels: taskInfo.labels,
        priority: taskInfo.priority,
        recurring: taskInfo.recurring?.string || null,
        assigneeUserId: teamTaskAssigneeUserId,
      });

      return !isEqual(current, initialFormSnapshot);
    }, [
      isEditMode,
      initialFormSnapshot,
      draftName,
      draftDesc,
      taskInfo.aiNote,
      taskInfo.dueDateTime,
      taskInfo.duration,
      taskInfo.labels,
      taskInfo.priority,
      taskInfo.recurring?.string,
      teamTaskAssigneeUserId,
    ]);
    const isSaveDisabled =
      isReadOnly ||
      isSavingTask ||
      isCreateSaving ||
      isSubmitting ||
      !draftName.trim() ||
      (isEditMode && !hasUnsavedChanges);

    const handleFooterSave = () => {
      if (isSubmittingRef.current || isSaveDisabled) {
        return;
      }
      void handleTask(draftName, draftDesc);
    };

    const handleDurationChange = (value: TaskItemDuration) => {
      setTaskInfo((prev) => ({ ...prev, duration: value }));
    };

    const handleLabelChange = (newLabels: TaskLabel[]) => {
      setTaskInfo((prev) => ({ ...prev, labels: newLabels }));
    };

    const handleAiNoteChange = (aiNote: string) => {
      setTaskInfo((prev) => ({ ...prev, aiNote }));
    };

    const handleDeleteConfirm = async () => {
      try {
        await handleDeleteTask(defaultTask.taskId);
        setDeleteConfirmOpen(false);
      } catch (error) {
        console.error('Failed to delete task:', error);
      }
    };

    const handleRepeatChange = useCallback(
      (newRepeatText: string | null) => {
        const newRecurring = newRepeatText
          ? {
              isRecurring: true,
              string: newRepeatText,
              lang: taskInfo.recurring?.lang || defaultTask?.recurring?.lang || 'en',
              timezone:
                taskInfo.recurring?.timezone ||
                taskInfo.dueDateTime?.timezone ||
                defaultTask?.recurring?.timezone ||
                dayjs.tz.guess(),
            }
          : undefined;

        setTaskInfo((prev) => ({ ...prev, recurring: newRecurring }));
      },
      [defaultTask?.recurring, taskInfo.recurring, taskInfo.dueDateTime?.timezone]
    );

    const handleTeamTaskAssigneeChange = useCallback((assigneeUserId: string) => {
      setTeamTaskAssigneeUserId(assigneeUserId);
    }, []);

    const handleDateChange = useCallback((dateSet: DateTimeSetOnlyTime) => {
      setTaskInfo((prev) => ({
        ...prev,
        dueDateTime: {
          timezone: dayjs.tz.guess(),
          datetime: dateSet.datetime,
          date: dateSet.date,
        },
      }));
    }, []);

    const handleClearDate = useCallback(() => {
      setTaskInfo((prev) => ({
        ...prev,
        dueDateTime: {
          timezone: dayjs.tz.guess(),
          datetime: '',
          date: '',
        },
      }));
      setAiSuggestedTask(null);
    }, []);

    const handleClearTime = useCallback(() => {
      setTaskInfo((prev) => {
        const nextDate = prev.dueDateTime?.datetime
          ? dayjs(prev.dueDateTime.datetime).format('YYYY-MM-DD')
          : prev.dueDateTime?.date!;
        return {
          ...prev,
          dueDateTime: {
            timezone: dayjs.tz.guess(),
            datetime: '',
            date: nextDate,
          },
        };
      });
      setAiSuggestedTask(null);
    }, []);

    const [creatingSubTask, setCreatingSubTask] = useState(false);
    const [updatingSubTaskId, setUpdatingSubTaskId] = useState<string | null>(null);

    const handleCreateSubTask = useCallback(
      async (options: AddTodoTaskItemProps) => {
        // Add subtask with temporary taskId first
        const taskId = uuidv4().replace(/-/g, '');
        setTaskInfo((prev) => ({
          ...prev,
          subTasks: [...prev.subTasks, { ...options, taskId }],
        }));

        // Create the actual task and update with real taskId
        setCreatingSubTask(true);
        try {
          await addTodoTask({
            boardId: taskInfo.boardId,
            newTaskInfo: options,
            taskId,
          });
        } finally {
          setCreatingSubTask(false);
        }
      },
      [addTodoTask, taskInfo.boardId]
    );

    const handleUpdateSubTask = useCallback(
      async (taskId: string, updates: TodoTaskUpdate) => {
        setUpdatingSubTaskId(taskId);
        try {
          await updateTodoTask({
            taskId,
            boardId: taskInfo.boardId,
            newTaskInfo: updates,
            shouldReflectToEvent: false,
          });
          setTaskInfo((prev) => ({
            ...prev,
            subTasks: prev.subTasks.map((subtask) =>
              subtask.taskId === taskId ? { ...subtask, ...updates } : subtask
            ),
          }));
        } finally {
          setUpdatingSubTaskId(null);
        }
      },
      [updateTodoTask, taskInfo.boardId]
    );

    const handleReorderSubTasks = useCallback(
      async (sourceId: string, targetId: string, position: 'before' | 'after') => {
        const sortedSubTasks = [...taskInfo.subTasks].sort(
          (a, b) => (a.childOrder || 0) - (b.childOrder || 0)
        );
        const sourceIndex = sortedSubTasks.findIndex((st) => st.taskId === sourceId);
        const targetIndex = sortedSubTasks.findIndex((st) => st.taskId === targetId);

        if (sourceIndex === -1 || targetIndex === -1 || sourceIndex === targetIndex) return;

        // Calculate new positions
        let newSubTasks = [...sortedSubTasks];
        const [movedTask] = newSubTasks.splice(sourceIndex, 1);

        // Insert at new position
        let insertIndex: number;
        if (position === 'before') {
          insertIndex = sourceIndex < targetIndex ? targetIndex - 1 : targetIndex;
        } else {
          insertIndex = sourceIndex < targetIndex ? targetIndex : targetIndex + 1;
        }
        newSubTasks.splice(insertIndex, 0, movedTask);

        // Update childOrder for all affected subtasks
        const updates: Array<{ taskId: string; childOrder: number }> = [];
        newSubTasks.forEach((subtask, index) => {
          const currentOrder = subtask.childOrder || 0;
          if (currentOrder !== index) {
            updates.push({ taskId: subtask.taskId, childOrder: index });
          }
        });

        // Update local state immediately
        setTaskInfo((prev) => ({
          ...prev,
          subTasks: newSubTasks.map((subtask, index) => ({
            ...subtask,
            childOrder: index,
          })),
        }));

        // Update all affected subtasks
        for (const update of updates) {
          const subtask = newSubTasks.find((st) => st.taskId === update.taskId);
          if (subtask) {
            try {
              await updateTodoTask({
                taskId: update.taskId,
                boardId: taskInfo.boardId,
                newTaskInfo: {
                  childOrder: update.childOrder,
                  appFrom: subtask.appFrom,
                },
                showSnackbar: false,
                shouldReflectToEvent: false,
              });
            } catch (error) {
              console.error(`Failed to update subtask ${update.taskId}:`, error);
            }
          }
        }
      },
      [taskInfo.subTasks, taskInfo.boardId, updateTodoTask]
    );

    const dateSet = useMemo(
      () => ({
        date: taskInfo.dueDateTime?.date,
        datetime: taskInfo.dueDateTime?.datetime,
      }),
      [taskInfo.dueDateTime?.date, taskInfo.dueDateTime?.datetime]
    );

    //@link: https://www.notion.so/demind-inc/Integrations-Compatibility-d2129356af5f45ed90f6bad4f67c54b7
    // Ticktick, Apple Reminders, and Notion do not support subtasks to be edited.
    const canEditSubTasks = useMemo(() => {
      if (isTeamTaskMode) {
        return false;
      }

      return (
        defaultTask?.appFrom !== 'ticktick' &&
        defaultTask?.appFrom !== 'apple-reminders' &&
        defaultTask?.appFrom !== 'notion'
      );
    }, [defaultTask, isTeamTaskMode]);

    return (
      <>
        <Modal
          open={visible}
          onClose={handleClose}
          className="task-detail-modal"
          disableRestoreFocus
        >
          <div
            className="task-detail-modal__container"
            onClick={(e) => {
              // Close modal when clicking on the backdrop (outside the panel)
              if (e.target === e.currentTarget) {
                handleClose();
              }
            }}
          >
            <div className="task-detail-modal__panel">
              {isMovingTaskBetweenProjects && (
                <div className="task-detail-modal__overlay">
                  <CircularProgress size={24} />
                </div>
              )}
              {resolvedReadOnlyBadgeText && (
                <div className="task-detail-modal__readonly-badge">{resolvedReadOnlyBadgeText}</div>
              )}
              <div
                className={clsx(
                  'task-detail-modal__panel-body',
                  isReadOnly && 'task-detail-modal__panel-body--readonly'
                )}
              >
                {isAppleRemindersReadOnly && (
                  <div className="task-detail-modal__header__warning">
                    {t('task.project.appleRemindersDesktopOnlyWithPeriod')}
                  </div>
                )}
                <TaskTitleDescriptionSection
                  name={draftName}
                  desc={draftDesc}
                  onNameChange={setDraftName}
                  isEditMode={isEditMode}
                  isReadOnly={isReadOnly}
                  completed={defaultTask?.completed || false}
                  showCheckbox={isEditMode && !!defaultTask?.taskId}
                  onSave={handleTask}
                  onCompletedChange={
                    isEditMode && defaultTask?.taskId && !isReadOnly
                      ? (completed) => handleUpdateTaskStatus(defaultTask.taskId, completed)
                      : undefined
                  }
                  disabledTooltipTitle={
                    isCompletedRecurringTask
                      ? t('task.modal.cannotReopenCompletedOccurrence')
                      : isReadOnlyProp
                        ? t('task.modal.teamTaskViewOnly')
                        : ''
                  }
                />

                <div className="task-detail-modal__fields">
                  <TaskScheduleSection
                    variant="inline"
                    task={{ ...defaultTask, ...taskInfo }}
                    dueDateTime={taskInfo.dueDateTime}
                    dateSet={dateSet}
                    duration={taskInfo.duration}
                    onDateChange={isReadOnly ? () => void 0 : handleDateChange}
                    onClearDate={
                      isReadOnly || selectedProject?.appFrom === 'ticktick'
                        ? undefined
                        : handleClearDate
                    }
                    onClearTime={isReadOnly ? undefined : handleClearTime}
                    onDurationChange={handleDurationChange}
                    disabled={isReadOnly}
                    timeFormat={timeFormat}
                    phase={phase}
                    taskTime={taskTime}
                    hideSmartScheduleButton={shouldHideSmartScheduleButton}
                  />

                  {!isTeamTaskMode && shouldShowRepeatSelector && (
                    <>
                      <hr className="task-detail-modal__divider" />
                      <TaskPropertyRow icon={<RepeatIcon className="task-detail-modal__icon" />}>
                        <RepeatSelect
                          value={taskInfo.recurring?.string || null}
                          onChange={handleRepeatChange}
                          dueDateTime={taskInfo.dueDateTime}
                          onDateChange={handleDateChange}
                          disabled={isReadOnly || !canEditRepeat}
                        />
                      </TaskPropertyRow>
                    </>
                  )}

                  <hr className="task-detail-modal__divider" />
                  <TaskPropertyRow
                    icon={
                      <PriorityFlag
                        priority={taskInfo.priority}
                        color={taskInfo.priority ? undefined : getCssVariable('--color-black')}
                        type={taskInfo.priority ? 'fill' : 'outline'}
                        size="small"
                        className="task-detail-modal__icon"
                      />
                    }
                  >
                    <PrioritySelect
                      value={taskInfo.priority}
                      onChange={(priority) => setTaskInfo((prev) => ({ ...prev, priority }))}
                      size="small"
                      disabled={isReadOnly}
                    />
                  </TaskPropertyRow>

                  {!isTeamTaskMode && (
                    <>
                      <hr className="task-detail-modal__divider" />
                      <TaskPropertyRow
                        icon={<Icon path={mdiTagOutline} className="task-detail-modal__icon" />}
                      >
                        <TaskLabelsSelect
                          allLabels={taskLabels}
                          selectedLabels={taskInfo.labels}
                          setSelectedLabels={handleLabelChange}
                          disabled={isReadOnly}
                        />
                      </TaskPropertyRow>
                    </>
                  )}

                  <hr className="task-detail-modal__divider" />
                  <TaskDescriptionField
                    value={draftDesc}
                    onChange={setDraftDesc}
                    isReadOnly={isReadOnly}
                  />

                  <hr className="task-detail-modal__divider" />
                  <TaskAiNoteSection
                    value={taskInfo.aiNote || ''}
                    isReadOnly={isReadOnly}
                    onChange={handleAiNoteChange}
                  />

                  {isTeamTaskMode && teamTaskOptions && (
                    <>
                      <hr className="task-detail-modal__divider" />
                      <TaskPropertyRow
                        icon={<PersonOutlineRounded className="task-detail-modal__icon" />}
                      >
                        <TeamTaskAssigneeSelect
                          members={teamTaskOptions.members}
                          currentUserId={teamTaskOptions.currentUserId}
                          value={teamTaskAssigneeUserId}
                          disabled={isReadOnly}
                          variant="modal"
                          onChange={(assigneeUserId) =>
                            void handleTeamTaskAssigneeChange(assigneeUserId)
                          }
                        />
                      </TaskPropertyRow>
                    </>
                  )}

                  <hr className="task-detail-modal__divider" />
                  <TaskPropertyRow
                    icon={<Icon path={mdiInboxOutline} className="task-detail-modal__icon" />}
                  >
                    <TaskProject
                      todoProjects={resolvedTodoProjects}
                      selectedProject={
                        selectedProject ||
                        (!isTeamTaskMode && visibleProjectIds?.[0]
                          ? findProject(visibleProjectIds[0])
                          : null)
                      }
                      onProjectSelect={handleProjectSelect}
                      onProjectAdd={() => {
                        if (isTeamTaskMode) {
                          teamTaskOptions?.onOpenCreateBoard?.();
                          return;
                        }

                        handleProjectDialogVisible(true);
                      }}
                      isDisabled={
                        isReadOnly || isMovingTaskBetweenProjects || (isTeamTaskMode && isEditMode)
                      }
                      currentAppFrom={defaultTask?.appFrom || selectedProject?.appFrom}
                      hasRecurring={hasRecurringRule}
                      hasCustomRecurrence={hasCustomRecurrence}
                      enableToAdd={!isTeamTaskMode || !!teamTaskOptions?.onOpenCreateBoard}
                    />
                  </TaskPropertyRow>
                </div>

                {isEditMode &&
                  defaultTask?.taskId &&
                  (canEditSubTasks || !!taskInfo.subTasks.length) && (
                    <TaskSubtasksSection
                      parentTask={defaultTask}
                      subtasks={taskInfo.subTasks}
                      canEdit={canEditSubTasks && !isReadOnly}
                      onCompleteSubTask={(taskId, completed) =>
                        handleUpdateTaskStatus(taskId, completed, defaultTask?.originalTaskId)
                      }
                      onUpdateSubTask={handleUpdateSubTask}
                      onCreateSubTask={handleCreateSubTask}
                      onDeleteSubTask={(taskId) =>
                        handleDeleteTask(taskId, defaultTask?.originalTaskId)
                      }
                      onReorderSubTasks={handleReorderSubTasks}
                      updatingSubTaskId={updatingSubTaskId}
                    />
                  )}
              </div>

              <div className="task-detail-modal__footer-sticky">
                <div className="task-detail-modal__footer">
                  {showDiscardButton ? (
                    <button
                      type="button"
                      className="task-detail-modal__footer__discard"
                      onClick={() => setDeleteConfirmOpen(true)}
                      aria-label={t('task.common.delete')}
                    >
                      <DeleteOutlined />
                      <span>{t('task.common.delete')}</span>
                    </button>
                  ) : (
                    <div />
                  )}
                  <div className="task-detail-modal__footer__actions">
                    <Button
                      onClick={handleCancel}
                      disabled={isReadOnly && !isEditMode}
                      variant="text"
                      className="task-detail-modal__footer__cancel"
                    >
                      {t('task.common.cancel')}
                    </Button>
                    <LoadingButton
                      loading={
                        (isSavingTask || isCreateSaving || isSubmitting) &&
                        !updatingSubTaskId &&
                        !creatingSubTask
                      }
                      disabled={isSaveDisabled}
                      onClick={handleFooterSave}
                      variant="contained"
                      className="task-detail-modal__footer__save"
                    >
                      {t('task.common.save')}
                    </LoadingButton>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal>

        <BasicDialog
          visible={deleteConfirmOpen}
          title={t('task.modal.deleteTask')}
          description={t('task.modal.deleteTaskDescription')}
          ctaString={t('task.common.delete')}
          isLoading={isDeletingVisibleTask}
          onYes={handleDeleteConfirm}
          onClose={() => setDeleteConfirmOpen(false)}
        />

        <AddProjectDialog
          isAdding={isAddingProject}
          visible={projectDialogVisible}
          existingProjectNames={todoProjects.map((p) => p.name)}
          onAdd={handleAddProject}
          onClose={() => handleProjectDialogVisible(false)}
        />
      </>
    );
  }
);

export default TaskDetailModal;
